import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import {
    AcquisitionStrategy,
    ActualDataType,
    BulkEditProperty,
    ComponentDefinition,
    ConnectionPoint,
    DigitalSet,
    ErrorLevel,
    FlatMode,
    IDisplayedColumn,
    IValueViewModel,
    Qualifier,
    SearchFormat,
    TagDefinition,
    TagDefinitionDTO,
    TagDefinitionVm,
    TimeUnit,
    Unit,
    VariableSubType,
    VariableType,
} from '../../../models';

import {
    Helper,
    Roles,
    WideError,
    defaultItemPerPage,
    groupIdentifierSeparator,
    guidEmpty,
    isEditMode,
    saveErrorMessage,
    tagDefaultState,
    tagProperties
} from '../../../common';

import {
    IAuthorizationService,
    IExportToCsvService,
    IFrequencyFormatHelperService,
    ILoggerService,
    ISaveMessagesService,
    IStoreService,
    ITagDefinitionMapper,
    ITagsFilteringService
} from '../../../services';

import { Store } from '../../../stateManagement/Store';
import { StoreElement } from '../../../stateManagement/StoreElement';
import { IRootScope } from '../../../IRootScope';
import { SearchEnable } from '../../../controls';
import { Dictionary } from 'lodash';
import { getDigitalConnectionPoints, getFloatConnectionPoints, getPreviousStepConnectionPoints, getSpotConnectionPoints } from '../tagsContextual/getTagsMiniatures';

const flatMode = 'Flat mode';

interface ISearchCondition {
    keyword: string;
    callback: (t: any, regExp: RegExp) => boolean;
}

class TagsStateController implements angular.IComponentController {
    bulkEditProperties: BulkEditProperty[];
    bulkEditTooltip: string;
    columns: IDisplayedColumn[];
    connectionPoints: ConnectionPoint[];
    digitalSets: DigitalSet[];
    digitalSetsDictionary: Dictionary<DigitalSet>;
    displayed: TagDefinitionVm[];
    displayedToReview: number;
    filtered: TagDefinitionVm[];
    formController: angular.IFormController;
    format: SearchFormat<any>;
    groups: any[]; // menu groups
    hasMore: boolean;
    isApplicationLocked: boolean;
    isEditMode: boolean;
    isLoading: boolean;
    itemsPerPage = defaultItemPerPage;
    loadingMessage: string;
    mayAcquire: boolean;
    mayEdit: boolean;
    menu: { model: IDisplayedColumn; label: string }[];
    mode: string;
    pageNumber = 1;
    searchConditions: ISearchCondition[];
    searchEnable: SearchEnable;
    searchString: string;
    selectedItem: TagDefinitionVm;
    tableForm: angular.xeditable.IEditableFormController;
    tagDefinitionList: TagDefinitionVm[];
    tagNames: string[];
    tags: TagDefinition[];
    editTagsTooltip: string;
    viewModels: Dictionary<IValueViewModel>;

    private _unsubscribe: Function;
    private _connectionPoints: Dictionary<ConnectionPoint>;
    private _variableTypes: Dictionary<VariableType>;
    private _variableSubTypes: Dictionary<VariableSubType>;
    private _qualifiers: Dictionary<Qualifier>;
    private _units: Dictionary<Unit>;
    private _allTags: Dictionary<TagDefinitionDTO>;
    private _newGuid: string;

    static $inject = [
        '$scope',
        '$rootScope',
        '$state',
        '$stateParams',
        '$translate',
        'authorizationService',
        'tagsFilteringService',
        'tagDefinitionMapper',
        'exportToCsvService',
        'frequencyFormatHelperService',
        'loggerService',
        'storeService',
        'saveMessagesService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$state: angular.ui.IStateService,
        private _$stateParams: angular.ui.IStateParamsService,
        private _$translate: angular.translate.ITranslateService,
        private _authorizationService: IAuthorizationService,
        private _tagsFilteringService: ITagsFilteringService,
        private _tagDefinitionMapper: ITagDefinitionMapper,
        private _exportToCsvService: IExportToCsvService,
        private _frequencyFormatHelperService: IFrequencyFormatHelperService,
        private _loggerService: ILoggerService,
        private _storeService: IStoreService,
        private _saveMessagesService: ISaveMessagesService
    ) { }

    $onInit() {
        this.searchEnable = { tag: true, identifier: true, severity: true };
        this.isEditMode = this._$rootScope.isEditMode;

        if (this._$stateParams['searchString'] !== undefined && this._$stateParams['searchString'] !== null) {
            if (this._$stateParams.searchString === '') {
                this._tagsFilteringService.setActiveFilter('');
                this.searchString = '';
            } else {
                this.searchString = this._$stateParams.searchString;
            }
        }

        if (this._$stateParams.mode) {
            this.mode = this._$stateParams.mode;
        } else {
            this.mode = 'tiles';
        }

        this.filtered = [];
        this.displayed = [];
        this.displayedToReview = 0;
        this.bulkEditProperties = _.sortBy(tagProperties, d => d.value);
        this.format = {
            identifier: f => f.identifierNames.join(','),
            tag: f => f.name,
            comment: f => f.shortComment,
            source: (f: TagDefinition) => this.getConnectionPointName(f.acquisitionPointGuid),
            variable: (f: TagDefinition) => {
                let name = '';
                if (f.variableTypeGuid) {
                    const variableType = this._variableTypes[f.variableTypeGuid];
                    if (variableType) {
                        name = variableType.name;
                    }
                }
                return name;
            },
            unit: (f: TagDefinition) => {
                let name = '';
                if (f.unitGuid) {
                    const unit = this._units[f.unitGuid];
                    if (unit) {
                        name = unit.name;
                    }
                }
                return name;
            },
            severity: (t: { errors: WideError[] }) => {
                let result = '';
                if (_.some(t.errors, (e: WideError) => e.level === ErrorLevel.Error)) {
                    result = 'error';
                } else if (_.some(t.errors, (e: WideError) => e.level === ErrorLevel.Warning)) {
                    result = 'warning';
                }
                return result;
            }
        };

        this._$scope.$watch(() => this.searchString, () => this.filter());

        this._$scope.$on(isEditMode, (event, data) => {
            if (this.tableForm && this.tableForm.$visible) {
                this.tableForm.$cancel();
            }
            this.isEditMode = data;
            this._newGuid = null;
        });

        this.mayAcquire = this._authorizationService.isInRoles(Roles.acquisitionRoles);
        this.mayEdit = this._authorizationService.canEdit();

        // init data
        this._unsubscribe = this._storeService.register(store => this.refreshStore(store), [
            StoreElement.ConnectionPoints,
            StoreElement.DigitalSets,
            StoreElement.Tags,
            StoreElement.TagsDto,
            StoreElement.Variables,
            StoreElement.ValueViewModels,
            StoreElement.Application
        ]);

        this.buildMenu();
    }

    $onDestroy() {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    drop(data: any): void {
        let dataType: ActualDataType;
        let strategy: AcquisitionStrategy;
        let cpGuid: string;
        switch (data.itemId) {
            case 'Float':
                dataType = ActualDataType.Float;
                strategy = AcquisitionStrategy.Default;
                cpGuid = getFloatConnectionPoints(this.connectionPoints)[0].guid;
                break;
            case 'Digital':
                dataType = ActualDataType.Digital;
                strategy = AcquisitionStrategy.Default;
                cpGuid = getDigitalConnectionPoints(this.connectionPoints)[0].guid;
                break;
            case 'PreviousStep':
                dataType = ActualDataType.Float;
                strategy = AcquisitionStrategy.PreviousStep;
                cpGuid = getPreviousStepConnectionPoints(this.connectionPoints)[0].guid;
                break;
            case 'Spot':
                dataType = ActualDataType.Float;
                strategy = AcquisitionStrategy.Spot;
                cpGuid = getSpotConnectionPoints(this.connectionPoints)[0].guid;
                break;
        }

        this._newGuid = Helper.getGuid();
        const newTag: any = {
            guid: this._newGuid,
            sourceUnitGuid: guidEmpty,
            actualDataType: dataType,
            acquisitionStrategy: strategy,
            resolution: 0,
            resolutionPercentage: 0,
            flatMode: (dataType === ActualDataType.Digital || dataType === ActualDataType.String)
                ? FlatMode.NoDetection
                : FlatMode.Normal,
            acquisitionPointGuid: cpGuid
        };
        (this.displayed as any[]).push(newTag);
        (this.tagDefinitionList as any[]).push(newTag);
        this.tagDefinitionList = _.sortBy(this.tagDefinitionList, t => t.name);
        this.selectedItem = newTag;
        this.bringIntoView(newTag.guid);
    }

    onSave() {
        const p = this._storeService.dispatchers().tags.update([this.selectedItem]);
        return this._saveMessagesService.saveTag(p)
            .then(() => false)
            .catch(() => true);
    }

    onCancel() {
        if (this.selectedItem.guid === this._newGuid) {
            this.refresh();
        }
        this.selectedItem = null;
        this._newGuid = null;
    }

    onBulkEdit(data: number) {
        this._loggerService.logSuccess(`${data} properties updated.`);
        this._storeService.dispatchers().tags.getAll(true);
    }

    exportToCsv() {
        const fileName = `Tags_${moment(new Date()).format('YYYY-MM-DD')}.csv`;
        const data = [this.getCsvHeader()];
        this.filtered.forEach(r => data.push(this.getCsvRow(r)));
        this._exportToCsvService.getCsvFromData(data, fileName);
    }

    updateDisplayedAsReviewed() {
        this.isLoading = true;
        const toReview = this.displayed.filter(t => t.isReviewRequired);

        toReview.forEach(t => {
            t.isReviewRequired = false;
            if (t.errors && t.errors.length > 0) {
                t.errors = t.errors.filter(e => e.level !== ErrorLevel.Warning);
            }
        });

        this._storeService.dispatchers().tags
            .update(toReview)
            .then(data => {
                this.updateDisplayed();
                this._loggerService.logSuccess(`Updated ${toReview.length} tag(s)`);
            })
            .catch(reason => {
                this._loggerService.logError(`Failed to update tags : ${reason}`);
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    changeGroup(item) {
        item.headers.forEach(h => {
            const found: any = _.find(this.columns, (c: any) => c.label === h);
            found.isVisible = item.model.isVisible;
        });
    }

    more() {
        this.pageNumber++;
        this.updateDisplayed();
    }

    less() {
        this.pageNumber--;
        this.updateDisplayed();
    }

    unselect($event) {
        if (this.isEditMode && this.formController && this.formController.$dirty) {
            const msg = this._$translate.instant(saveErrorMessage);
            this._loggerService.logWarning(msg);
            $event.stopPropagation();
        } else {
            if (!$event.isDefaultPrevented()) {
                this.setSelectedItem(null);
            }
        }
    }

    setSelectedItem(item) {
        this.selectedItem = item;
    }

    selectItem(item, $event) {
        if (item || this.isEditMode) {
            if (this.isEditMode) {
                if (this.formController.$dirty) {
                    const msg = this._$translate.instant(saveErrorMessage);
                    this._loggerService.logWarning(msg);
                    $event.stopPropagation();
                } else {
                    this.selectedItem = item;
                }
            } else {
                const tag = this._allTags[item.configuration.guid];
                this._$state.go(tagDefaultState, { tagDefinitionGuid: item.configuration.guid, tag: tag });
            }
            $event.preventDefault();
        }
    }

    canCreateGroup() {
        return this._authorizationService.canEdit();
    }

    canEdit() {
        return this._authorizationService.canEdit() && this.mode === 'table' && !this._$rootScope.isEditMode;
    }

    setMode(mode: string) {
        this.mode = mode;
    }

    stopPropagation(event: angular.IAngularEvent): void {
        event.stopPropagation();
    }

    deleteItem(guid) {
        this.isLoading = true;
        const promise = this._storeService.dispatchers().tags.delete([guid]);
        this.afterDelete(promise);
    }

    deleteUnused() {
        this.isLoading = true;
        const guids = this.getUnused().map(t => t.guid);
        this.afterDelete(this._storeService.dispatchers().tags.delete(guids));
    }

    getUnused() {
        return this.displayed.filter(d => !d.isUsed);
    }

    onDuplicate(name: string) {
        const guid = Helper.getGuid();
        const toAdd = Object.assign({}, this.selectedItem, { name, guid });
        this.isLoading = true;
        this._storeService.dispatchers().tags
            .create(toAdd)
            .then(() => this.selectedItem = toAdd)
            .catch(reason => console.error(reason))
            .finally(() => this.isLoading = false);
    }

    private filter() {
        this.filtered = this._tagsFilteringService
            .filterResults(this.tagDefinitionList, this.searchString, groupIdentifierSeparator, this.format);
        this.updateDisplayed();
    }

    private getLagLabel(tag: TagDefinition, self: TagsStateController): string {
        return self._frequencyFormatHelperService.formatFrequency(tag.lag);
    }

    private getValidityPeriodLabel(tag: TagDefinition, self: TagsStateController): string {
        return tag.validityInterval === 0 || tag.validityInterval === undefined
            ? ''
            : self._frequencyFormatHelperService.formatFrequency({
                interval: tag.validityInterval,
                intervalTimeUnit: tag.validityUnit
            });
    }

    private buildMenu() {
        // Do not change order or you must change order in table template for <tr>
        const props: { label: string, property: string, accessor?: (i, self?) => string, isExportable: boolean }[] = [
            { label: 'Long comment', property: 'longComment', isExportable: true },
            { label: 'Documentation', property: 'documentation', isExportable: true },
            { label: 'Type', property: 'variableTypeName', isExportable: true },
            { label: 'Subtype', property: 'subtypeName', isExportable: true },
            { label: 'Qualifier', property: 'qualifierName', isExportable: true },
            { label: 'Source unit', property: 'sourceUnitName', isExportable: true },
            { label: 'Unit', property: 'unitName', isExportable: true },
            { label: 'PFD', property: 'pfd', isExportable: true },
            { label: 'PID', property: 'pid', isExportable: true },
            { label: 'Data sheet', property: 'dataSheet', isExportable: true },
            { label: 'Default value', property: 'defaultValue', isExportable: true },
            { label: 'Min', property: 'min', isExportable: true },
            { label: 'Max', property: 'max', isExportable: true },
            { label: 'Std dev threshold', property: 'stdDevThreshold', isExportable: true },
            { label: flatMode, property: 'flatMode', isExportable: true },
            { label: 'Resolution', property: 'resolution', isExportable: true },
            { label: 'Resolution%', property: 'resolutionPercentage', isExportable: true },
            { label: 'Source', property: 'connectionPointName', isExportable: true },
            { label: 'Validity period', property: 'validityPeriod', accessor: this.getValidityPeriodLabel, isExportable: false },
            { label: 'Offset', property: 'lag', accessor: this.getLagLabel, isExportable: false },
            { label: 'Own fields', property: 'customProperties', accessor: () => '', isExportable: false }
        ];

        this.columns = [];
        this.menu = [];
        props.forEach((p, i) => {
            this.columns.push({
                isVisible: false,
                label: p.label,
                property: p.property,
                type: '',
                accessor: p.accessor,
                isExportable: p.isExportable
            });
            this.menu.push({
                model: this.columns[i],
                label: p.label
            });
        });
        const g = [
            {
                model: { isVisible: true },
                label: 'Default',
                headers: ['Type', 'Subtype', 'Qualifier', 'Unit', 'Default value', 'Min', 'Max', 'Std dev threshold', 'Source']
            }, {
                model: { isVisible: false },
                label: 'Documentation',
                headers: ['Long comment', 'Documentation', 'PFD', 'PID', 'Data sheet']
            }, {
                model: { isVisible: true },
                label: 'Statistics',
                headers: ['Default value', 'Min', 'Max', 'Std dev threshold', flatMode, 'Resolution', 'Resolution%']
            }
        ];
        this.groups = g;
        this.changeGroup(g[0]);
        this.changeGroup(g[2]);
    }

    private getCsvHeader(): string[] {
        let header: string[] = [];

        header.push('GUID');
        header.push('Name');
        header.push('Short comment');
        header.push('Strategy');
        header.push('Data type');
        header.push('Digital set');
        _.forEach(this.columns, c => {
            if (c.isExportable) {
                header.push((c as any).label);
            }
        });
        header.push('Requires review');
        header.push('Ignored');
        header.push('Validity period interval');
        header.push('Validity period unit');
        header.push('Offset interval');
        header.push('Offset unit');
        header = header.concat(this._exportToCsvService.getUserFieldsHeaders(this.filtered));
        return header;
    }

    private getCsvRow(tag: TagDefinitionVm) {
        let digitalSetName = '';
        if (tag.actualDataType === ActualDataType.Digital) {
            const digitalSet = this.digitalSetsDictionary[tag.digitalSetGuid];
            digitalSetName = digitalSet?.name;
        }
        const self = this;
        let row = [];
        row.push(tag.guid);
        row.push(tag.name);
        row.push(tag.shortComment);
        row.push(AcquisitionStrategy[tag.acquisitionStrategy]);
        row.push(ActualDataType[tag.actualDataType]);
        row.push(digitalSetName);
        _.forEach(this.columns, c => {
            if (c.isExportable) {
                let value = c.accessor
                    ? c.accessor(tag, self)
                    : tag[c.property];
                if (c.label === flatMode) {
                    value = FlatMode[value];
                }
                row.push(value);
            }
        });
        row.push(tag.isReviewRequired);
        row.push(tag.isIgnored);
        row.push(tag.validityInterval);
        row.push(TimeUnit[tag.validityUnit]);
        row.push(tag.lag.interval);
        row.push(TimeUnit[tag.lag.intervalTimeUnit]);
        row = row.concat(this._exportToCsvService.getUserFieldsValues(tag.customProperties));
        return row;
    }

    private getConnectionPointName(guid) {
        let name: string;
        if (guid === guidEmpty) {
            name = '';
        } else {
            const connectionPoint = _.find(this._connectionPoints, c => c.guid === guid);
            if (connectionPoint) {
                name = connectionPoint.name;
            } else {
                name = '';
            }
        }
        return name;
    }

    private updateDisplayed() {
        const min = Math.min(this.pageNumber * this.itemsPerPage, this.filtered.length);
        this.displayed = this.filtered.slice(0, min);
        this.hasMore = (min < this.filtered.length);
        this.displayedToReview = this.displayed.filter(t => t.isReviewRequired).length;
    }

    private refresh() {
        this.isLoading = true;

        const list = this._tagDefinitionMapper.mapTagsWithAll(
            _.values(this._allTags),
            this._connectionPoints,
            this._variableTypes,
            this._variableSubTypes,
            this._qualifiers,
            this._units,
            this.viewModels,
            this.digitalSetsDictionary
        );

        const filtered = this._tagsFilteringService.filterResults(
            list,
            this.searchString,
            groupIdentifierSeparator,
            this.format
        );
        this.tagNames = list.map(d => d.name);
        this.tagDefinitionList = list;
        this.filtered = filtered;
        this.updateDisplayed();
        if (this.selectedItem && this.displayed) {
            let found = this.displayed.find(tag => tag.guid === this.selectedItem.guid);
            if (found) {
                this.selectedItem = found;
            } else {
                // If you created a new element, it might not be visible; however, it might be selected... So let's add it to the visible ones
                found = list.find(t => t.guid === this.selectedItem.guid);
                if (found) {
                    this.displayed = this.displayed.concat([found])
                        .sort((a, b) => a.name < b.name ? -1 : 1);
                    this.selectedItem = found;
                    this.bringIntoView(found.guid);
                }
            }
        }
        this.isLoading = false;
    }

    private bringIntoView(guid: string) {
        const listener = this._$scope.$watch(() => {
            const element = document.getElementById(`item${guid}`);
            if (element) {
                element.scrollIntoView();
                listener();
            }
        });
    }

    private refreshStore(store: Store): void {
        this.connectionPoints = _.values(store.connectionPoints.dictionary);
        this.digitalSets = _.values(store.digitalSets.dictionary);
        this.digitalSetsDictionary = store.digitalSets.dictionary;
        this.tags = _.values(store.tags.dictionary);
        this._connectionPoints = store.connectionPoints.dictionary;
        this._variableTypes = store.variablesTypes.dictionary;
        this._variableSubTypes = store.variableSubTypes.dictionary;
        this._qualifiers = store.qualifiers.dictionary;
        this._units = store.units.dictionary;
        this._allTags = store.tagsDto.dictionary;
        this.viewModels = store.valueViewModels.dictionary;
        this.isApplicationLocked = store.application?.value?.isLocked;
        this.bulkEditTooltip = this.getBulkEditTooltip();
        this.editTagsTooltip = this.getEditTagsTooltip();
        this.refresh();
    }

    private afterDelete(promise: angular.IPromise<TagDefinition[]>) {
        promise
            .then(data => {
                this.selectedItem = null;
                this._loggerService.logSuccess('Tag(s) successfully deleted.');
                this.isLoading = false;
            })
            .catch(reason => {
                this.isLoading = false;
            });
    }    

    private getEditTagsTooltip(): string {
        return this.isApplicationLocked
            ? 'Edit mode is disabled'
            : 'Edit tags';
    }

    private getBulkEditTooltip(): string {
        return this.isApplicationLocked
            ? 'Edit mode is disabled'
            : 'Bulk edit';
    }
}

export const wtTagsState: ComponentDefinition = {
    componentId: 'wtTagsState',
    controller: TagsStateController,
    template: require('./tagsState.component.html'),
    controllerAs: 'vm',
    bindings: {
    }
};